import React, { useMemo, useState, useEffect } from 'react';
import { Box, Popover, Button, Grid, Typography, Divider, CircularProgress } from '@mui/material';
import { Translate } from '@mui/icons-material';
import useInteractiveText from '../../hooks/useInteractiveText';
import WordCard from '../Learning/WordCard';
import RateLimitDialog from '../Common/RateLimitDialog';
import { userRequest } from '../../requestMethods';
import PropTypes from 'prop-types';

const InteractiveTextViewer = ({ 
    formattedText, 
    textId, 
    startCount = 0,
    baseClassName = 'interactive-text-viewer',
    style = {},
    showInteractiveStyles = true,
    source = null,
    onWordDisplayed = null,
    enableTranslation = true
}) => {
    // Translation state
    const [showTranslation, setShowTranslation] = useState(false);
    const [wholeTextTranslation, setWholeTextTranslation] = useState(null);
    const [translationLoading, setTranslationLoading] = useState(false);
    const [cachedTextId, setCachedTextId] = useState(textId);
    // Cache for storing translations by textId
    const [translationCache, setTranslationCache] = useState(new Map());

    // Generate unique className for this instance
    const uniqueClassName = useMemo(() => {
        return `${baseClassName}-${textId.replace(/[^a-zA-Z0-9]/g, '')}-${Math.random().toString(36).substr(2, 9)}`;
    }, [baseClassName, textId]);

    const {
        clickedWord,
        anchorEl,
        open,
        handleClose,
        rateLimitError,
        setRateLimitError
    } = useInteractiveText(formattedText, textId, uniqueClassName, startCount, showTranslation ? 1 : 0);

    // Fetch whole text translation with caching
    const fetchWholeTextTranslation = async (targetTextId = textId) => {
        if (!targetTextId) return null;
        
        // Check if translation is already cached
        if (translationCache.has(targetTextId)) {
            console.log('Using cached translation for textId:', targetTextId);
            const cachedTranslation = translationCache.get(targetTextId);
            setWholeTextTranslation(cachedTranslation);
            return cachedTranslation;
        }
        
        try {
            setTranslationLoading(true);
            console.log('Fetching new translation for textId:', targetTextId);
            const response = await userRequest.get(`/text/wholeTranslation/${targetTextId}`);
            const translation = response.data;
            
            // Cache the translation
            setTranslationCache(prevCache => {
                const newCache = new Map(prevCache);
                newCache.set(targetTextId, translation);
                return newCache;
            });
            
            setWholeTextTranslation(translation);
            return translation;
        } catch (error) {
            console.error('Error fetching whole text translation:', error);
            // Cache the fact that no translation is available
            setTranslationCache(prevCache => {
                const newCache = new Map(prevCache);
                newCache.set(targetTextId, null);
                return newCache;
            });
            setWholeTextTranslation(null);
            return null;
        } finally {
            setTranslationLoading(false);
        }
    };

    // Handle textId changes for captions
    useEffect(() => {
        if (textId !== cachedTextId) {
            setCachedTextId(textId);
            
            if (showTranslation) {
                // If translation is currently shown, get translation for new textId
                // This will use cache if available, or fetch if not
                fetchWholeTextTranslation(textId);
            } else {
                // If translation is hidden, check if we have cached translation
                if (translationCache.has(textId)) {
                    // Set cached translation but don't show it
                    setWholeTextTranslation(translationCache.get(textId));
                } else {
                    // No cached translation, clear current translation
                    setWholeTextTranslation(null);
                }
            }
        }
    }, [textId, showTranslation, cachedTextId, translationCache]);

    const defaultStyle = {
        lineHeight: 1.8,
        fontSize: '1.1rem',
        fontFamily: 'Georgia, serif',
        textAlign: 'justify',
        ...style
    };

    // Handle translation toggle
    const handleToggleTranslation = async () => {
        if (!showTranslation) {
            // About to show translation - fetch if not cached
            await fetchWholeTextTranslation(textId);
        }
        setShowTranslation(!showTranslation);
    };

    return (
        <Box>
            {/* Interactive Styles */}
            {showInteractiveStyles && (
                <style>
                    {`
                        .${uniqueClassName} .chp {
                            display: inline;
                            padding: 2px 1px;
                            border-radius: 2px;
                            transition: background-color 0.2s;
                            cursor: pointer;
                        }
                        .${uniqueClassName} .chp:hover {
                            background-color: rgba(25, 118, 210, 0.1);
                        }
                    `}
                </style>
            )}

            {/* Translation Toggle Button */}
            {enableTranslation && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Button
                        variant="text"
                        size="small"
                        startIcon={<Translate />}
                        onClick={handleToggleTranslation}
                        disabled={translationLoading}
                        sx={{ 
                            fontSize: '0.875rem',
                            textTransform: 'none',
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                    >
                        {translationLoading ? (
                            <>
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                Loading...
                            </>
                        ) : showTranslation ? (
                            'Hide Translation'
                        ) : (
                            'Show Translation'
                        )}
                    </Button>
                </Box>
            )}

            {/* Content Layout */}
            {!showTranslation ? (
                /* Single Column - Original Content */
                <div 
                    className={uniqueClassName}
                    style={defaultStyle}
                    dangerouslySetInnerHTML={{ __html: formattedText }}
                />
            ) : (
                /* Two Column - Side by Side with Translation */
                <Grid container spacing={2}>
                    {/* Original Text Column */}
                    <Grid item xs={6}>
                        <Box sx={{ p: 2, minHeight: '100%' }}>
                            <Typography 
                                variant="h6" 
                                gutterBottom 
                                sx={{ 
                                    fontWeight: 'bold', 
                                    color: 'primary.main',
                                    textAlign: 'center',
                                    fontSize: '1rem',
                                    mb: 1
                                }}
                            >
                                Original
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            
                            <div 
                                className={uniqueClassName}
                                style={{
                                    ...defaultStyle,
                                    fontSize: '0.95rem'
                                }}
                                dangerouslySetInnerHTML={{ __html: formattedText }}
                            />
                        </Box>
                    </Grid>
                    
                    {/* Translation Column */}
                    <Grid item xs={6}>
                        <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 1, p: 2, minHeight: '100%' }}>
                            <Typography 
                                variant="h6" 
                                gutterBottom 
                                sx={{ 
                                    fontWeight: 'bold', 
                                    color: 'secondary.main',
                                    fontSize: '1rem',
                                    textAlign: 'center',
                                    mb: 1
                                }}
                            >
                                {wholeTextTranslation?.translationLanguage?.name || 'Translation'}
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            
                            {wholeTextTranslation ? (
                                <Box
                                    sx={{
                                        lineHeight: 1.8,
                                        fontSize: '0.95rem',
                                        fontFamily: 'Georgia, serif',
                                        textAlign: 'justify',
                                        '& p': { 
                                            fontSize: '0.95rem !important',
                                            fontFamily: 'Georgia, serif',
                                            lineHeight: 1.8,
                                            margin: '0 0 16px 0'
                                        },
                                        '& *': { 
                                            fontFamily: 'Georgia, serif',
                                            fontSize: '0.95rem !important',
                                            lineHeight: 1.8
                                        }
                                    }}
                                    dangerouslySetInnerHTML={{ __html: wholeTextTranslation.wholeText }}
                                />
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography 
                                        variant="body1" 
                                        color="text.secondary"
                                        sx={{ fontSize: '0.95rem' }}
                                    >
                                        Translation not available
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        color="text.secondary" 
                                        sx={{ 
                                            mt: 1,
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        This text has not been translated yet
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            )}

            {/* Word Details Popover */}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                disableRestoreFocus={true}
                disableEnforceFocus={true}
                disableAutoFocus={true}
                BackdropProps={{
                    invisible: true
                }}
                PaperProps={{
                    elevation: 8,
                    sx: {
                        maxWidth: { xs: 280, sm: 300, md: 400 },
                        width: { xs: '90vw', sm: 'auto' },
                        border: '1px solid #e0e0e0',
                        // Ensure the popover stays within viewport bounds
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }
                }}
                // Add container prop to ensure better positioning
                container={typeof document !== 'undefined' ? document.body : undefined}
                // Enable better positioning for mobile
                marginThreshold={16}
            >
                {clickedWord && <WordCard word={clickedWord} onClose={handleClose} source={source} onWordDisplayed={onWordDisplayed} />}
            </Popover>

            {/* Rate Limit Dialog */}
            <RateLimitDialog 
                open={!!rateLimitError}
                onClose={() => setRateLimitError(null)}
                errorData={rateLimitError}
            />
        </Box>
    );
};

InteractiveTextViewer.propTypes = {
    formattedText: PropTypes.string.isRequired,
    textId: PropTypes.string.isRequired,
    startCount: PropTypes.number,
    baseClassName: PropTypes.string,
    style: PropTypes.object,
    showInteractiveStyles: PropTypes.bool,
    source: PropTypes.object,
    onWordDisplayed: PropTypes.func,
    enableTranslation: PropTypes.bool
};

export default InteractiveTextViewer; 